<template>
  <div class="responsive-wrapper relative">
    <!-- Show indication labels in editor mode for blocks hidden in the current viewwidth -->
    <VisualEditorPill :class="inverseResponsiveClasses">
      Hidden <span class="font-bold">{{ component }}</span> block on
      <span>{{ deviceList }}</span>
    </VisualEditorPill>
    <div v-if="shouldRender" :class="responsiveClasses">
      <slot />
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue'
import VisualEditorPill from './VisualEditorPill.vue'

const props = defineProps({
  isResponsive: {
    type: Boolean,
    default: true,
  },
  component: {
    type: String,
    default: '',
  },
  devices: {
    type: Array,
    default: () => ['mobile', 'tablet', 'desktop'],
    validator: (devices) => {
      return devices.every((device) =>
        ['mobile', 'tablet', 'desktop'].includes(device),
      )
    },
  },
})

const availableDevices = [
  { name: 'mobile', prefix: '' },
  { name: 'tablet', prefix: 'md:' },
  { name: 'desktop', prefix: 'lg:' },
]

const responsiveClasses = computed(() => {
  if (!props.isResponsive) {
    return ''
  }
  // To make sure dynamically defined css classes are not purged, it's enough to add them as comment. md:block lg:block md:hidden lg:hidden
  return availableDevices.map((device) =>
    props.devices.includes(device.name)
      ? `${device.prefix}block`
      : `${device.prefix}hidden`,
  )
})
const inverseResponsiveClasses = computed(() => {
  if (!props.devices.length || !props.isResponsive) {
    return 'hidden'
  }
  return availableDevices.map((device) =>
    props.devices.includes(device.name)
      ? `${device.prefix}hidden`
      : `${device.prefix}block`,
  )
})
const deviceList = computed(() => {
  const capitalizedDevices = props.devices.map((device) =>
    device.replace(/^\w/, (c) => c.toUpperCase()),
  )
  if (capitalizedDevices.length === 1) {
    return capitalizedDevices[0]
  }
  return (
    capitalizedDevices.slice(0, -1).join(', ') +
    ' and ' +
    capitalizedDevices.slice(-1)
  )
})
const shouldRender = computed(() => {
  return !!import.meta.env.VITE_IS_EDITOR || props.devices?.length > 0
})
</script>

<style scoped>
@reference "../css/theme.css";

.editor-annotation:before {
  content: '';
  @apply absolute top-3 -left-4 h-0.5 w-4 bg-cyan-500;
}
</style>
